import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { payInvoiceInfo, payInvoiceCheckout, payStatus } from '../services/api';
import { Button } from '../components/ui/button';
import { Loader2, Check, X, Lock, CreditCard, AlertCircle, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const fmt = (n, c = 'EUR') => new Intl.NumberFormat('es-ES', { useGrouping: 'always', style: 'currency', currency: c }).format(n || 0);

const PaymentPage = () => {
  const { token } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await payInvoiceInfo(token);
      setInfo(r.data);
    } catch (e) {
      setError(e.response?.status === 404 ? 'Enlace de pago inválido' : 'No se pudo cargar la factura');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const pollSession = useCallback(async (sid, attempts = 0) => {
    if (attempts >= 8) {
      setPaymentStatus({ status: 'timeout' });
      return;
    }
    try {
      const r = await payStatus(sid);
      setPaymentStatus(r.data);
      if (r.data.paid) return;
      if (r.data.status === 'expired') return;
      setTimeout(() => pollSession(sid, attempts + 1), 2500);
    } catch (e) {
      setPaymentStatus({ status: 'error', message: 'Error consultando estado' });
    }
  }, []);

  useEffect(() => {
    load();
    if (sessionId) {
      pollSession(sessionId);
    }
  }, [load, pollSession, sessionId]);

  const handlePay = async () => {
    setProcessing(true);
    try {
      const r = await payInvoiceCheckout(token, window.location.origin);
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error iniciando pago');
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <Loader2 className="w-10 h-10 animate-spin text-[#FF6B4A]" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 p-6">
        <div className="bg-white rounded-2xl shadow-lg p-10 max-w-md text-center" data-testid="pay-error">
          <Lock className="w-16 h-16 mx-auto text-stone-300 mb-4" />
          <h1 className="text-xl font-bold mb-2">Enlace no disponible</h1>
          <p className="text-stone-500">{error}</p>
        </div>
      </div>
    );
  }

  const paid = info?.paid || paymentStatus?.paid;

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-stone-100 flex items-center justify-center p-4" data-testid="payment-page">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-br from-[#FF6B4A] to-[#FBBF24] p-6 text-white text-center">
            <Sparkles className="w-8 h-8 mx-auto mb-2 opacity-90" />
            <p className="text-sm opacity-90">Pago seguro</p>
            <p className="text-3xl font-bold mt-1">{fmt(info.total, info.currency)}</p>
            <p className="text-sm opacity-90 mt-1">Factura {info.invoice_number}</p>
          </div>

          {/* Body */}
          <div className="p-6 space-y-4">
            {paid ? (
              <div className="text-center py-6" data-testid="payment-success">
                <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                  <Check className="w-10 h-10 text-emerald-600" />
                </div>
                <p className="text-xl font-bold text-stone-900">¡Pago recibido!</p>
                <p className="text-sm text-stone-500 mt-1">Gracias por tu pago. Recibirás un recibo por email en breve.</p>
              </div>
            ) : paymentStatus && !paymentStatus.paid && sessionId ? (
              <div className="text-center py-6">
                <Loader2 className="w-12 h-12 mx-auto text-[#FF6B4A] animate-spin mb-3" />
                <p className="text-stone-700">Verificando pago...</p>
                <p className="text-xs text-stone-400 mt-1">Esto suele tardar unos segundos</p>
              </div>
            ) : (
              <>
                <div className="space-y-2 text-sm">
                  <Row label="Empresa" value={info.company_name} />
                  <Row label="Cliente" value={info.client_name} />
                  <Row label="Fecha emisión" value={info.issue_date} />
                  {info.due_date && <Row label="Vencimiento" value={info.due_date} />}
                </div>

                <Button
                  onClick={handlePay}
                  disabled={processing}
                  className="w-full bg-stone-900 text-white hover:bg-stone-800 h-12 text-base font-semibold"
                  data-testid="pay-button"
                >
                  {processing ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <CreditCard className="w-5 h-5 mr-2" />}
                  Pagar con tarjeta · {fmt(info.total, info.currency)}
                </Button>

                {info.company_iban && (
                  <div className="bg-stone-50 rounded-xl p-3">
                    <p className="text-xs uppercase text-stone-500 font-semibold mb-1">O por transferencia</p>
                    <p className="text-sm font-mono text-stone-700">{info.company_iban}</p>
                    <p className="text-xs text-stone-500 mt-1">Indica como concepto: {info.invoice_number}</p>
                  </div>
                )}

                <p className="text-[10px] text-center text-stone-400 flex items-center justify-center gap-1">
                  <Lock className="w-3 h-3" /> Pago procesado por Stripe. No guardamos datos de tu tarjeta.
                </p>
              </>
            )}
          </div>
        </div>
        <p className="text-center text-xs text-stone-400 mt-4">Powered by Facturcal</p>
      </div>
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex justify-between">
    <span className="text-stone-500">{label}</span>
    <span className="text-stone-800 font-medium">{value || '—'}</span>
  </div>
);

export default PaymentPage;
